<template>
  <div class="crb">
    <!-- ── Header ──────────────────────────────────────────────────── -->
    <header class="crb-head">
      <div>
        <span class="crb-eyebrow">Demandes clients</span>
        <h1 class="crb-title">{{ isRib ? "Changements de RIB" : "Changements de téléphone" }}</h1>
        <p class="crb-sub">
          {{ isRib
            ? "Vérifiez le nouveau RIB et les justificatifs avant de valider : la validation remplace le RIB du magasin et du profil client."
            : "Vérifiez le nouveau numéro avant de valider : la validation remplace le téléphone du magasin et du profil client." }}
        </p>
      </div>
      <nav v-if="canRib && canPhone" class="crb-switch" aria-label="Type de demande">
        <router-link to="/rib-requests-list" :class="{ 'is-active': isRib }"><i class="ph ph-bank" aria-hidden="true"></i>RIB</router-link>
        <router-link to="/phone-requests-list" :class="{ 'is-active': !isRib }"><i class="ph ph-phone" aria-hidden="true"></i>Téléphone</router-link>
      </nav>
    </header>

    <!-- ── Status tabs ─────────────────────────────────────────────── -->
    <div class="crb-tabs" role="tablist" aria-label="Filtrer par statut">
      <button
        v-for="t in statusTabs"
        :key="t.value"
        type="button"
        role="tab"
        class="crb-tab"
        :class="['tone-' + t.tone, { 'is-active': filters.statut === t.value }]"
        :aria-selected="filters.statut === t.value"
        @click="setStatut(t.value)"
      >
        <i class="ph" :class="t.icon" aria-hidden="true"></i>
        <span>{{ t.label }}</span>
        <b>{{ t.count }}</b>
      </button>
    </div>

    <!-- ── Toolbar ─────────────────────────────────────────────────── -->
    <div class="crb-toolbar">
      <div class="crb-search">
        <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
        <input
          v-model="filters.term"
          type="search"
          :placeholder="isRib ? 'Client, magasin, RIB, banque…' : 'Client, magasin, numéro…'"
          aria-label="Rechercher"
          @input="onSearch"
        />
      </div>

      <div class="crb-period">
        <button type="button" class="crb-chip-btn" :class="{ 'is-set': filters.range }" :aria-expanded="periodOpen" @click="periodOpen = !periodOpen">
          <i class="ph ph-calendar-blank" aria-hidden="true"></i>{{ periodLabel }}
          <i class="ph ph-caret-down" aria-hidden="true"></i>
        </button>
        <template v-if="periodOpen">
          <div class="crb-backdrop" @click="periodOpen = false"></div>
          <DateRangePopover :model-value="filters.range" :preset="filters.preset" @apply="applyPeriod" @close="periodOpen = false" />
        </template>
      </div>

      <select v-if="isRib" v-model="filters.bank" class="form-select crb-select" aria-label="Banque" @change="fetchData(1)">
        <option value="">Toutes les banques</option>
        <option v-for="b in banks" :key="b.id" :value="String(b.id)">{{ b.name }}</option>
      </select>

      <button v-if="hasFilters" type="button" class="crb-reset" @click="resetFilters">
        <i class="ph ph-x" aria-hidden="true"></i>Réinitialiser
      </button>
    </div>

    <!-- ── List ────────────────────────────────────────────────────── -->
    <section class="crb-list" :aria-busy="loading">
      <template v-if="loading && !rows.length">
        <span v-for="n in 4" :key="n" class="crb-skel"></span>
      </template>
      <div v-else-if="loadError" class="crb-state is-error" role="alert">
        <i class="ph ph-warning-circle" aria-hidden="true"></i>
        <p>Impossible de charger les demandes.</p>
        <button type="button" class="btn btn-sm btn-light" @click="fetchData(pager.current_page)">Réessayer</button>
      </div>
      <div v-else-if="!rows.length" class="crb-state">
        <i class="ph" :class="filters.statut === '0' ? 'ph-check-circle' : 'ph-tray'" aria-hidden="true"></i>
        <p>{{ filters.statut === "0" && !hasFilters ? "Aucune demande en attente. Tout est traité 🎉" : "Aucune demande ne correspond aux filtres." }}</p>
      </div>

      <ul v-else class="crb-rows" :class="{ 'is-refreshing': loading }">
        <li v-for="row in rows" :key="row.id">
          <button type="button" class="crb-row" :class="{ 'is-selected': selected && selected.id === row.id }" @click="openReview(row)">
            <span class="crb-avatar" aria-hidden="true">
              <img v-if="row.client?.logo" :src="row.client.logo" alt="" referrerpolicy="no-referrer" @error="($event.target as HTMLImageElement).remove()" />
              <span>{{ initials(row.customer_name || row.client?.name) }}</span>
            </span>

            <span class="crb-who">
              <strong>{{ row.customer_name || row.client?.name || "Client #" + row.customer }}</strong>
              <small><i class="ph ph-storefront" aria-hidden="true"></i>{{ row.magasin?.name || "Magasin #" + row.magasin_id }}</small>
            </span>

            <span class="crb-diff">
              <span class="crb-old" :title="'Actuel : ' + display(row.old_rib)">{{ display(row.old_rib) || "—" }}</span>
              <i class="ph ph-arrow-right" aria-hidden="true"></i>
              <span class="crb-new">{{ display(row.new_rib) }}</span>
              <small v-if="isRib && row.bank_name" class="crb-bank">{{ row.bank_name }}</small>
              <small v-if="isRib && row.account_holder" class="crb-bank">{{ row.account_holder }}</small>
              <small v-if="!isRib" class="crb-bank">{{ phoneKindLabel(row) }}</small>
            </span>

            <span v-if="isRib" class="crb-docs" :aria-label="docsSummary(row)">
              <i
                v-for="d in docKinds"
                :key="d.key"
                class="ph"
                :class="[d.icon, row.has_docs?.[d.key] ? 'is-on' : 'is-off']"
                :title="d.label + (row.has_docs?.[d.key] ? ' fourni' : ' manquant')"
                aria-hidden="true"
              ></i>
            </span>

            <span class="crb-when">
              <span class="crb-status" :class="'st-' + row.statut">{{ statusLabel(row.statut) }}</span>
              <small :class="{ 'is-late': isLate(row) }" :title="fullDate(row.created_at)">{{ ago(row.created_at) }}</small>
            </span>
            <i class="ph ph-caret-right crb-go" aria-hidden="true"></i>
          </button>
        </li>
      </ul>

      <nav v-if="pager.last_page > 1" class="crb-pager" aria-label="Pagination">
        <button type="button" :disabled="pager.current_page <= 1" aria-label="Page précédente" @click="fetchData(pager.current_page - 1)">
          <i class="ph ph-caret-left" aria-hidden="true"></i>
        </button>
        <span>Page <b>{{ pager.current_page }}</b> / {{ pager.last_page }} · {{ pager.total }} demande(s)</span>
        <button type="button" :disabled="pager.current_page >= pager.last_page" aria-label="Page suivante" @click="fetchData(pager.current_page + 1)">
          <i class="ph ph-caret-right" aria-hidden="true"></i>
        </button>
      </nav>
    </section>

    <!-- ── Review drawer ───────────────────────────────────────────── -->
    <transition name="crb-fade">
      <div v-if="selected" class="crb-scrim" @click="closeReview"></div>
    </transition>
    <transition name="crb-slide">
      <aside v-if="selected" ref="drawerEl" class="crb-drawer" role="dialog" aria-modal="true" aria-labelledby="crb-drawer-title" tabindex="-1" @keydown.esc="closeReview">
        <header class="crb-drawer-head">
          <div>
            <span class="crb-status" :class="'st-' + selected.statut">{{ statusLabel(selected.statut) }}</span>
            <h2 id="crb-drawer-title">{{ selected.customer_name || selected.client?.name }}</h2>
            <p>
              <i class="ph ph-storefront" aria-hidden="true"></i>{{ selected.magasin?.name || "Magasin #" + selected.magasin_id }}
              <span aria-hidden="true">·</span> Demande #{{ selected.id }} du {{ fullDate(selected.created_at) }}
            </p>
          </div>
          <button type="button" class="crb-x" aria-label="Fermer" @click="closeReview"><i class="ph ph-x" aria-hidden="true"></i></button>
        </header>

        <div class="crb-drawer-body">
          <!-- Change -->
          <section class="crb-block">
            <h3>Changement demandé</h3>
            <p v-if="!isRib" class="crb-note">
              <i class="ph" :class="isPersonal(selected) ? 'ph-user' : 'ph-briefcase'" aria-hidden="true"></i>
              <span>Type de numéro à modifier : <b>{{ isPersonal(selected) ? "Personnel" : "Professionnel" }}</b></span>
            </p>
            <p v-else-if="selected.account_holder" class="crb-note">
              <i class="ph ph-user" aria-hidden="true"></i>
              <span>Titulaire du compte : <b>{{ selected.account_holder }}</b></span>
            </p>
            <p v-else class="crb-note is-warn"><i class="ph ph-warning" aria-hidden="true"></i>Titulaire du compte non renseigné.</p>
            <div class="crb-compare">
              <div>
                <span>Actuel</span>
                <code>{{ display(currentValue(selected)) || "—" }}</code>
                <small v-if="isRib && selected.magasin?.bank">{{ selected.magasin.bank }}</small>
              </div>
              <i class="ph ph-arrow-down" aria-hidden="true"></i>
              <div class="is-new">
                <span>Nouveau</span>
                <code>{{ display(selected.new_rib) }}</code>
                <small v-if="isRib">{{ selected.bank_name }}</small>
                <button type="button" class="crb-copy" :aria-label="'Copier ' + (isRib ? 'le RIB' : 'le numéro')" @click="copy(selected.new_rib)">
                  <i class="ph ph-copy" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <blockquote v-if="selected.raison" class="crb-reason">
              <i class="ph ph-quotes" aria-hidden="true"></i>{{ selected.raison }}
            </blockquote>
          </section>

          <!-- Automatic checks -->
          <section class="crb-block">
            <h3>Contrôles</h3>
            <ul class="crb-checks">
              <li v-for="c in checks" :key="c.label" :class="'is-' + c.state">
                <i class="ph" :class="c.state === 'ok' ? 'ph-check-circle' : c.state === 'warn' ? 'ph-warning' : 'ph-x-circle'" aria-hidden="true"></i>
                <span>{{ c.label }}</span>
              </li>
            </ul>
          </section>

          <!-- Documents -->
          <section v-if="isRib" class="crb-block">
            <h3>Justificatifs</h3>
            <p v-if="!docsEnabled" class="crb-note is-warn">
              <i class="ph ph-plug" aria-hidden="true"></i>Consultation indisponible : la clé REQUEST_DOCS_KEY n'est pas configurée sur le serveur.
            </p>
            <div class="crb-docgrid">
              <div v-for="d in docKinds" :key="d.key" class="crb-doc" :class="{ 'is-missing': !selected.has_docs?.[d.key] }">
                <template v-if="selected.has_docs?.[d.key] && selected.docs?.[d.key]">
                  <button type="button" class="crb-doc-open" :aria-label="'Ouvrir ' + d.label" @click="openDoc(d.key)">
                    <img
                      v-if="!docFailed[d.key]"
                      :src="selected.docs[d.key]"
                      :alt="d.label"
                      referrerpolicy="no-referrer"
                      loading="lazy"
                      @error="docFailed[d.key] = true"
                    />
                    <span v-else class="crb-doc-file"><i class="ph ph-file-pdf" aria-hidden="true"></i>Ouvrir</span>
                  </button>
                </template>
                <span v-else class="crb-doc-file"><i class="ph" :class="d.icon" aria-hidden="true"></i>{{ selected.has_docs?.[d.key] ? "Indisponible" : "Non fourni" }}</span>
                <small>{{ d.label }}</small>
              </div>
            </div>
          </section>

          <!-- Decision (pending) -->
          <section v-if="Number(selected.statut) === 0" class="crb-block">
            <h3>Décision</h3>

            <details class="crb-fix" :open="fixOpen" @toggle="fixOpen = ($event.target as HTMLDetailsElement).open">
              <summary><i class="ph ph-pencil-simple" aria-hidden="true"></i>Corriger avant de valider</summary>
              <div v-if="isRib" class="crb-fix-grid">
                <label>
                  <span>RIB (24 chiffres)</span>
                  <input v-model="fix.rib" type="text" inputmode="numeric" class="form-control crb-mono" maxlength="32" :placeholder="selected.new_rib" />
                </label>
                <label>
                  <span>Banque</span>
                  <select v-model="fix.bank_id" class="form-select">
                    <option value="">{{ selected.bank_name || "—" }} (demandée)</option>
                    <option v-for="b in banks" :key="b.id" :value="String(b.id)">{{ b.name }}{{ b.code ? " · " + b.code : "" }}</option>
                  </select>
                </label>
              </div>
              <label v-else class="crb-fix-grid">
                <span>Numéro</span>
                <input v-model="fix.phone" type="tel" class="form-control crb-mono" maxlength="20" :placeholder="display(selected.new_rib)" />
              </label>
            </details>

            <label class="crb-label" for="crb-note">
              {{ mode === "refuse" ? "Motif du refus (visible par le client)" : "Commentaire (facultatif, visible par le client)" }}
              <span v-if="mode === 'refuse'" class="crb-req" aria-hidden="true">*</span>
            </label>
            <div v-if="mode === 'refuse'" class="crb-quick">
              <button v-for="q in quickReasons" :key="q" type="button" @click="note = q">{{ q }}</button>
            </div>
            <textarea
              id="crb-note"
              ref="noteEl"
              v-model="note"
              class="form-control"
              :class="{ 'is-invalid': noteError }"
              rows="3"
              maxlength="500"
              @input="noteError = ''"
            ></textarea>
            <span v-if="noteError" class="crb-err">{{ noteError }}</span>
            <span class="crb-count">{{ note.length }}/500</span>
          </section>

          <!-- Decision (processed) -->
          <section v-else class="crb-block">
            <h3>Décision</h3>
            <p class="crb-note" :class="Number(selected.statut) === 1 ? 'is-ok' : 'is-bad'">
              <i class="ph" :class="Number(selected.statut) === 1 ? 'ph-seal-check' : 'ph-prohibit'" aria-hidden="true"></i>
              {{ Number(selected.statut) === 1 ? "Validée" : "Refusée" }} le {{ fullDate(selected.updated_at) }}
            </p>
            <blockquote v-if="selected.statut_note" class="crb-reason"><i class="ph ph-chat-text" aria-hidden="true"></i>{{ selected.statut_note }}</blockquote>
          </section>
        </div>

        <footer v-if="Number(selected.statut) === 0" class="crb-drawer-foot">
          <template v-if="mode === 'refuse'">
            <button type="button" class="btn btn-light" :disabled="busy" @click="mode = 'approve'">Annuler</button>
            <button type="button" class="btn btn-danger" :disabled="busy" @click="refuse">
              <span v-if="busy" class="spinner-border spinner-border-sm" role="status"></span>
              Confirmer le refus
            </button>
          </template>
          <template v-else>
            <button type="button" class="btn btn-outline-danger" :disabled="busy" @click="startRefuse">
              <i class="ph ph-x" aria-hidden="true"></i>Refuser
            </button>
            <button type="button" class="btn btn-primary" :disabled="busy || hasBlockingCheck" @click="approve">
              <span v-if="busy" class="spinner-border spinner-border-sm" role="status"></span>
              <i v-else class="ph ph-check" aria-hidden="true"></i>Valider le changement
            </button>
          </template>
        </footer>
      </aside>
    </transition>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, PropType, reactive, ref, watch } from "vue";
import moment from "moment";
import Swal from "sweetalert2";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";
import { useAuthStore } from "@/stores/auth";
import DateRangePopover from "@/components/Common/DateRangePopover.vue";

moment.locale("fr");

type Kind = "RIB" | "PHONE";
type DocKey = "cin1" | "cin2" | "attest";

interface Bank {
  id: number;
  name: string;
  code: string | null;
}
interface RequestRow {
  id: number;
  customer: number;
  customer_name: string | null;
  type: Kind;
  raison: string | null;
  old_rib: string | null;
  new_rib: string | null;
  bank_id: number | null;
  bank_name: string | null;
  account_holder: string | null;
  phone_type: "professional" | "personal" | null;
  statut: number | string;
  statut_note: string | null;
  magasin_id: number;
  created_at: string;
  updated_at: string;
  magasin?: { id: number; name: string; phone: string | null; rib: string | null; bank: string | null; id_bank: number | null } | null;
  client?: { id: number; name: string; phone: string | null; phone_personal: string | null; logo: string | null; rib: string | null; bank: string | null } | null;
  docs?: Record<DocKey, string | null>;
  has_docs?: Record<DocKey, boolean>;
}

const DOC_TTL_MS = 9 * 60 * 1000; // server links live 10 minutes

const digits = (v: unknown) => String(v ?? "").replace(/\D+/g, "");

/** 011 780 0000123456789012 34 */
export function formatRib(v: unknown) {
  const d = digits(v);
  if (d.length !== 24) return String(v ?? "");
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 22)} ${d.slice(22)}`;
}

/** +212612345678 → 06 12 34 56 78 */
export function formatPhone(v: unknown) {
  const d = digits(v);
  const local = d.startsWith("212") && d.length === 12 ? "0" + d.slice(3) : d.length === 9 ? "0" + d : d;
  return local.length === 10 ? local.replace(/(\d{2})(?=\d)/g, "$1 ") : String(v ?? "");
}

export default defineComponent({
  name: "ClientRequestsBoard",
  components: { DateRangePopover },
  props: {
    type: { type: String as PropType<Kind>, required: true },
  },
  setup(props) {
    const { showToast } = useAppToast();
    const auth = useAuthStore();
    const isRib = computed(() => props.type === "RIB");
    const canRib = computed(() => auth.can("REQUEST_RIB"));
    const canPhone = computed(() => auth.can("REQUEST_PHONE"));

    const docKinds: { key: DocKey; label: string; icon: string }[] = [
      { key: "cin1", label: "CIN recto", icon: "ph-identification-card" },
      { key: "cin2", label: "CIN verso", icon: "ph-identification-badge" },
      { key: "attest", label: "Attestation RIB", icon: "ph-file-text" },
    ];

    // ── Data ──
    const rows = ref<RequestRow[]>([]);
    const loading = ref(false);
    const loadError = ref(false);
    const docsEnabled = ref(true);
    const counts = reactive({ pending: 0, approved: 0, refused: 0 });
    const pager = reactive({ current_page: 1, last_page: 1, total: 0, per_page: 15 });
    const banks = ref<Bank[]>([]);
    let fetchedAt = 0;
    let fetchSeq = 0;

    const filters = reactive({
      statut: "0",
      term: "",
      bank: "",
      range: null as [moment.Moment, moment.Moment] | null,
      preset: "",
    });

    const hasFilters = computed(() => !!(filters.term || filters.bank || filters.range));

    async function fetchData(page = 1) {
      const seq = ++fetchSeq;
      loading.value = true;
      loadError.value = false;
      try {
        const res = await api.get(isRib.value ? "/api/auth/RibRequests" : "/api/auth/PhoneRequests", {
          params: {
            page,
            per_page: pager.per_page,
            searchStatut: filters.statut,
            searchTearm: filters.term.trim() || undefined,
            searchBank: isRib.value ? filters.bank || undefined : undefined,
            searchSDate: filters.range ? filters.range[0].format("YYYY-MM-DD") : undefined,
            searchEDate: filters.range ? filters.range[1].format("YYYY-MM-DD") : undefined,
          },
        });
        if (seq !== fetchSeq) return;
        const data = res.data?.users || {};
        rows.value = data.data || [];
        Object.assign(counts, data.counts || {});
        docsEnabled.value = data.docs_enabled !== false;
        pager.current_page = data.current_page || 1;
        pager.last_page = data.last_page || 1;
        pager.total = data.total || 0;
        fetchedAt = Date.now();
        // keep the open request in sync (fresh document links)
        if (selected.value) {
          const fresh = rows.value.find((r) => r.id === selected.value?.id);
          if (fresh) selected.value = fresh;
        }
      } catch (error) {
        if (seq !== fetchSeq) return;
        console.error(error);
        loadError.value = true;
      } finally {
        if (seq === fetchSeq) loading.value = false;
      }
    }

    async function fetchBanks() {
      if (!isRib.value || banks.value.length) return;
      try {
        const res = await api.get("/api/auth/bank_list");
        banks.value = (res.data || []).map((b: Bank) => ({ id: b.id, name: b.name, code: b.code }));
      } catch (error) {
        console.error(error);
      }
    }

    onMounted(() => {
      fetchData(1);
      fetchBanks();
    });
    // the two routes share this component: reload when switching
    watch(
      () => props.type,
      () => {
        closeReview();
        rows.value = [];
        filters.bank = "";
        fetchData(1);
        fetchBanks();
      }
    );

    const statusTabs = computed(() => [
      { value: "0", label: "À traiter", icon: "ph-hourglass-medium", tone: "warn", count: counts.pending },
      { value: "1", label: "Validées", icon: "ph-seal-check", tone: "ok", count: counts.approved },
      { value: "2", label: "Refusées", icon: "ph-prohibit", tone: "bad", count: counts.refused },
      { value: "", label: "Toutes", icon: "ph-list-bullets", tone: "neutral", count: counts.pending + counts.approved + counts.refused },
    ]);

    function setStatut(v: string) {
      filters.statut = v;
      fetchData(1);
    }

    let searchTimer: number | undefined;
    function onSearch() {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => fetchData(1), 350);
    }

    // ── Period ──
    const periodOpen = ref(false);
    const periodLabel = computed(() => {
      if (!filters.range) return "Toute période";
      const [a, b] = filters.range;
      return a.isSame(b, "day") ? a.format("D MMM YYYY") : `${a.format("D MMM")} – ${b.format("D MMM YYYY")}`;
    });
    function applyPeriod(payload: { range: [moment.Moment, moment.Moment] | null; preset: string }) {
      filters.range = payload.range;
      filters.preset = payload.preset;
      periodOpen.value = false;
      fetchData(1);
    }
    function resetFilters() {
      filters.term = "";
      filters.bank = "";
      filters.range = null;
      filters.preset = "";
      fetchData(1);
    }

    // ── Display helpers ──
    const display = (v: unknown) => (v ? (isRib.value ? formatRib(v) : formatPhone(v)) : "");
    const initials = (name?: string | null) =>
      (name || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0] || "")
        .join("")
        .toUpperCase();
    const statusLabel = (s: number | string) => ({ 0: "En attente", 1: "Validée", 2: "Refusée" } as Record<string, string>)[String(s)] || "—";
    const ago = (d: string) => moment(d).fromNow();
    const fullDate = (d: string) => moment(d).format("DD/MM/YYYY [à] HH:mm");
    const isLate = (r: RequestRow) => Number(r.statut) === 0 && moment().diff(moment(r.created_at), "hours") >= 48;
    const docsSummary = (r: RequestRow) =>
      docKinds.map((d) => `${d.label} ${r.has_docs?.[d.key] ? "fourni" : "manquant"}`).join(", ");
    const isPersonal = (r: RequestRow) => r.phone_type === "personal";
    const phoneKindLabel = (r: RequestRow) => (isPersonal(r) ? "Numéro personnel" : "Numéro professionnel");
    const currentValue = (r: RequestRow) =>
      isRib.value
        ? r.magasin?.rib || r.client?.rib || r.old_rib
        : isPersonal(r)
        ? r.client?.phone_personal || r.old_rib
        : r.magasin?.phone || r.client?.phone || r.old_rib;

    async function copy(v: unknown) {
      try {
        await navigator.clipboard.writeText(digits(v) || String(v ?? ""));
        showToast("Copié dans le presse-papiers.", "success");
      } catch {
        showToast("Copie impossible.", "error");
      }
    }

    // ── Review drawer ──
    const selected = ref<RequestRow | null>(null);
    const drawerEl = ref<HTMLElement | null>(null);
    const noteEl = ref<HTMLTextAreaElement | null>(null);
    const mode = ref<"approve" | "refuse">("approve");
    const note = ref("");
    const noteError = ref("");
    const busy = ref(false);
    const fixOpen = ref(false);
    const fix = reactive({ rib: "", bank_id: "", phone: "" });
    const docFailed = reactive<Record<DocKey, boolean>>({ cin1: false, cin2: false, attest: false });
    let lastFocus: HTMLElement | null = null;

    function openReview(row: RequestRow) {
      lastFocus = document.activeElement as HTMLElement;
      selected.value = row;
      mode.value = "approve";
      note.value = "";
      noteError.value = "";
      fixOpen.value = false;
      Object.assign(fix, { rib: "", bank_id: "", phone: "" });
      Object.assign(docFailed, { cin1: false, cin2: false, attest: false });
      document.body.style.overflow = "hidden";
      nextTick(() => drawerEl.value?.focus());
      // stale document links: refresh the page of results
      if (Date.now() - fetchedAt > DOC_TTL_MS) fetchData(pager.current_page);
    }
    function closeReview() {
      if (!selected.value) return;
      selected.value = null;
      document.body.style.overflow = "";
      lastFocus?.focus?.();
    }
    onBeforeUnmount(() => {
      document.body.style.overflow = "";
      window.clearTimeout(searchTimer);
    });

    async function openDoc(key: DocKey) {
      if (Date.now() - fetchedAt > DOC_TTL_MS) await fetchData(pager.current_page);
      const url = selected.value?.docs?.[key];
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else showToast("Document indisponible.", "error");
    }

    const banksById = computed(() => new Map(banks.value.map((b) => [String(b.id), b])));

    /** Values that will actually be saved (request + optional correction). */
    const effective = computed(() => {
      const r = selected.value;
      if (!r) return { value: "", bank: null as Bank | null };
      if (isRib.value) {
        const rib = digits(fix.rib) || digits(r.new_rib);
        const bank =
          banksById.value.get(fix.bank_id) ||
          banksById.value.get(String(r.bank_id)) ||
          banks.value.find((b) => b.code === rib.slice(0, 3)) ||
          null;
        return { value: rib, bank };
      }
      return { value: digits(fix.phone) || digits(r.new_rib), bank: null };
    });

    const checks = computed(() => {
      const r = selected.value;
      if (!r) return [];
      const out: { label: string; state: "ok" | "warn" | "bad"; blocking?: boolean }[] = [];
      const current = digits(currentValue(r));
      if (isRib.value) {
        const { value: rib, bank } = effective.value;
        out.push({ label: rib.length === 24 ? "RIB de 24 chiffres" : `RIB invalide (${rib.length} chiffres au lieu de 24)`, state: rib.length === 24 ? "ok" : "bad", blocking: rib.length !== 24 });
        const codeOk = !!bank && !!bank.code && bank.code === rib.slice(0, 3);
        out.push({
          label: codeOk ? `Code banque ${rib.slice(0, 3)} = ${bank?.name}` : `Le code ${rib.slice(0, 3)} ne correspond pas à ${bank?.name || "la banque"}`,
          state: codeOk ? "ok" : "bad",
          blocking: !codeOk && banks.value.length > 0,
        });
        out.push({ label: rib !== current ? "Différent du RIB actuel" : "Identique au RIB actuel", state: rib !== current ? "ok" : "warn" });
        const missing = docKinds.filter((d) => !r.has_docs?.[d.key]).map((d) => d.label);
        out.push({ label: missing.length ? `Manquant : ${missing.join(", ")}` : "Tous les justificatifs sont fournis", state: missing.length ? "warn" : "ok" });
      } else {
        const d = effective.value.value;
        const local = d.startsWith("212") ? d.slice(3) : d.replace(/^0/, "");
        const valid = /^[5-7]\d{8}$/.test(local);
        out.push({ label: valid ? "Numéro marocain valide" : "Numéro invalide : corrigez-le", state: valid ? "ok" : "bad", blocking: !valid });
        out.push({ label: local !== current.replace(/^212/, "").replace(/^0/, "") ? "Différent du numéro actuel" : "Identique au numéro actuel", state: local !== current.replace(/^212/, "").replace(/^0/, "") ? "ok" : "warn" });
      }
      const days = moment().diff(moment(r.created_at), "days");
      if (Number(r.statut) === 0 && days >= 2) out.push({ label: `En attente depuis ${days} jours`, state: "warn" });
      return out;
    });
    const hasBlockingCheck = computed(() => checks.value.some((c) => c.blocking));

    const quickReasons = computed(() =>
      isRib.value
        ? ["Attestation RIB manquante ou illisible", "Le titulaire du compte ne correspond pas au client", "CIN manquante ou illisible", "RIB incorrect"]
        : ["Numéro injoignable", "Numéro déjà utilisé par un autre compte", "Informations insuffisantes"]
    );

    function startRefuse() {
      mode.value = "refuse";
      noteError.value = "";
      nextTick(() => noteEl.value?.focus());
    }

    function applyError(error: any, fallback: string) {
      const status = error?.response?.status;
      const msg = error?.response?.data?.message || fallback;
      showToast(msg, "error");
      // already processed elsewhere: refresh
      if (status === 409 || status === 404) {
        closeReview();
        fetchData(pager.current_page);
      }
    }

    async function approve() {
      const r = selected.value;
      if (!r || busy.value || hasBlockingCheck.value) return;
      const { value, bank } = effective.value;
      const store = r.magasin?.name || "du magasin";
      const intro =
        isRib.value || !isPersonal(r)
          ? `<p style="margin:0 0 8px">Le ${isRib.value ? "RIB" : "téléphone professionnel"} du magasin <b>${escapeHtml(store)}</b> et du profil client sera remplacé par :</p>`
          : `<p style="margin:0 0 8px">Seul le <b>téléphone personnel</b> du profil client sera remplacé par :</p>`;
      const confirm = await Swal.fire({
        title: isRib.value ? "Valider ce nouveau RIB ?" : "Valider ce nouveau numéro ?",
        html: `${intro}
               <p style="font-family:ui-monospace,monospace;font-weight:800;font-size:1.05rem;margin:0">${escapeHtml(display(value))}</p>
               ${bank && isRib.value ? `<p style="margin:4px 0 0;color:#6b7280">${escapeHtml(bank.name)}</p>` : ""}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Oui, valider",
        cancelButtonText: "Annuler",
        reverseButtons: true,
        focusCancel: true,
      });
      if (!confirm.isConfirmed) return;

      busy.value = true;
      try {
        const payload: Record<string, string> = {};
        if (note.value.trim()) payload.statut_note = note.value.trim();
        if (isRib.value) {
          if (digits(fix.rib)) payload.rib = digits(fix.rib);
          if (fix.bank_id) payload.bank_id = fix.bank_id;
        } else if (digits(fix.phone)) {
          payload.new_phone = fix.phone;
        }
        const res = await api.post(`/api/auth/${isRib.value ? "TraitRequestRib" : "TraitRequestPhone"}/${r.id}`, payload);
        if (res.data?.success || res.data?.msg === "success") {
          showToast("Demande validée : le client est notifié.", "success");
          closeReview();
          fetchData(pager.current_page);
        } else {
          showToast(res.data?.message || "La validation a échoué.", "error");
        }
      } catch (error) {
        applyError(error, "La validation a échoué.");
      } finally {
        busy.value = false;
      }
    }

    async function refuse() {
      const r = selected.value;
      if (!r || busy.value) return;
      const text = note.value.trim();
      if (text.length < 5) {
        noteError.value = "Indiquez le motif du refus (5 caractères minimum).";
        noteEl.value?.focus();
        return;
      }
      busy.value = true;
      try {
        const res = await api.post(`/api/auth/${isRib.value ? "CancelRequestRib" : "CancelRequestPhone"}/${r.id}`, { statut_note: text });
        if (res.data?.success || res.data?.msg === "success") {
          showToast("Demande refusée : le client est notifié.", "success");
          closeReview();
          fetchData(pager.current_page);
        } else {
          showToast(res.data?.message || "Le refus a échoué.", "error");
        }
      } catch (error) {
        applyError(error, "Le refus a échoué.");
      } finally {
        busy.value = false;
      }
    }

    function escapeHtml(s: string) {
      return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
    }

    return {
      isRib,
      canRib,
      canPhone,
      docKinds,
      rows,
      loading,
      loadError,
      docsEnabled,
      pager,
      banks,
      filters,
      hasFilters,
      statusTabs,
      setStatut,
      onSearch,
      fetchData,
      periodOpen,
      periodLabel,
      applyPeriod,
      resetFilters,
      display,
      initials,
      statusLabel,
      ago,
      fullDate,
      isLate,
      docsSummary,
      currentValue,
      isPersonal,
      phoneKindLabel,
      copy,
      selected,
      drawerEl,
      noteEl,
      mode,
      note,
      noteError,
      busy,
      fixOpen,
      fix,
      docFailed,
      openReview,
      closeReview,
      openDoc,
      checks,
      hasBlockingCheck,
      quickReasons,
      startRefuse,
      approve,
      refuse,
    };
  },
});
</script>

<style scoped>
.crb {
  padding-bottom: var(--ui-space-6);
}

/* Header */
.crb-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--ui-space-4);
  margin-bottom: var(--ui-space-5);
}
.crb-eyebrow {
  display: block;
  margin-bottom: 4px;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-eyebrow);
  text-transform: uppercase;
}
.crb-title {
  margin: 0;
  font-size: clamp(1.6rem, 2.6vw, 2.2rem);
  font-weight: var(--ui-fw-black);
  letter-spacing: -0.02em;
}
.crb-sub {
  max-width: 680px;
  margin: 6px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.crb-switch {
  display: inline-flex;
  padding: 4px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
}
.crb-switch a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: var(--ui-radius-pill);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  text-decoration: none;
  transition: all var(--ui-dur-fast) var(--ui-ease);
}
.crb-switch a.is-active {
  background: var(--ui-accent);
  color: var(--ui-on-accent, #fff);
  box-shadow: 0 6px 16px rgba(var(--ui-accent-rgb), 0.28);
}

/* Status tabs */
.crb-tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: var(--ui-space-4);
}
.crb-tab {
  --tone: var(--ui-text-secondary);
  --tone-soft: var(--ui-surface-sunken);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  text-align: left;
  transition: all var(--ui-dur-fast) var(--ui-ease);
}
.crb-tab i {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: var(--ui-radius-md);
  background: var(--tone-soft);
  color: var(--tone);
  font-size: 18px;
}
.crb-tab b {
  margin-left: auto;
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
}
.crb-tab.tone-warn { --tone: var(--ui-warning-strong); --tone-soft: var(--ui-warning-soft); }
.crb-tab.tone-ok { --tone: var(--ui-success); --tone-soft: var(--ui-success-soft); }
.crb-tab.tone-bad { --tone: var(--ui-danger); --tone-soft: var(--ui-danger-soft); }
.crb-tab:hover {
  border-color: var(--ui-border-strong);
}
.crb-tab.is-active {
  border-color: var(--tone);
  box-shadow: 0 0 0 3px var(--tone-soft);
}

/* Toolbar */
.crb-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: var(--ui-space-4);
}
.crb-search {
  position: relative;
  flex: 1 1 260px;
  max-width: 420px;
}
.crb-search i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ui-text-faint);
  pointer-events: none;
}
.crb-search input {
  width: 100%;
  height: 42px;
  padding: 0 14px 0 38px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  outline: none;
}
.crb-search input:focus {
  border-color: var(--ui-accent);
  box-shadow: var(--ui-focus-ring);
}
.crb-period {
  position: relative;
}
.crb-chip-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 14px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.crb-chip-btn.is-set {
  border-color: var(--ui-accent);
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}
.crb-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
}
.crb-select {
  width: auto;
  min-width: 200px;
  height: 42px;
  border-radius: var(--ui-radius-pill);
}
.crb-reset {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border: 0;
  background: none;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.crb-reset:hover {
  color: var(--ui-danger);
}

/* List */
.crb-list {
  padding: var(--ui-space-4);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.crb-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  transition: opacity var(--ui-dur) var(--ui-ease);
}
.crb-rows.is-refreshing {
  opacity: 0.55;
}
.crb-row {
  display: grid;
  grid-template-columns: 44px minmax(150px, 1.1fr) minmax(220px, 2fr) auto 130px 16px;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface);
  color: var(--ui-text);
  text-align: left;
  transition: all var(--ui-dur-fast) var(--ui-ease);
}
.crb-row:hover,
.crb-row.is-selected {
  border-color: var(--ui-accent);
  background: var(--ui-surface-sunken);
}
.crb-row:focus-visible {
  outline: none;
  box-shadow: var(--ui-focus-ring);
}
.crb-avatar {
  position: relative;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
  overflow: hidden;
}
.crb-avatar img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: var(--ui-surface);
}
.crb-who {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.crb-who strong {
  overflow: hidden;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.crb-who small {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.crb-diff {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  min-width: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: var(--ui-fs-xs);
}
.crb-old {
  color: var(--ui-text-faint);
  text-decoration: line-through;
}
.crb-diff > i {
  color: var(--ui-text-faint);
}
.crb-new {
  color: var(--ui-text);
  font-weight: var(--ui-fw-black);
}
.crb-bank {
  flex-basis: 100%;
  color: var(--ui-text-muted);
  font-family: var(--ui-font);
  font-weight: var(--ui-fw-bold);
}
.crb-docs {
  display: inline-flex;
  gap: 4px;
  font-size: 18px;
}
.crb-docs .is-on {
  color: var(--ui-success);
}
.crb-docs .is-off {
  color: var(--ui-text-faint);
  opacity: 0.45;
}
.crb-when {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.crb-when small {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.crb-when small.is-late {
  color: var(--ui-warning-strong);
  font-weight: var(--ui-fw-bold);
}
.crb-go {
  color: var(--ui-text-faint);
}
.crb-status {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: var(--ui-radius-pill);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  white-space: nowrap;
}
.crb-status.st-0 { background: var(--ui-warning-soft); color: var(--ui-warning-strong); }
.crb-status.st-1 { background: var(--ui-success-soft); color: var(--ui-success); }
.crb-status.st-2 { background: var(--ui-danger-soft); color: var(--ui-danger); }

.crb-skel {
  display: block;
  height: 68px;
  margin-bottom: 8px;
  border-radius: var(--ui-radius-md);
  background: linear-gradient(90deg, var(--ui-surface-sunken), var(--ui-surface-hover), var(--ui-surface-sunken));
  background-size: 300% 100%;
  animation: crb-shimmer 1.2s ease-in-out infinite;
}
@keyframes crb-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
.crb-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  color: var(--ui-text-muted);
  text-align: center;
}
.crb-state i {
  color: var(--ui-accent-text);
  font-size: 34px;
}
.crb-state p {
  margin: 0;
  font-size: var(--ui-fs-sm);
}
.crb-state.is-error i {
  color: var(--ui-danger);
}
.crb-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: var(--ui-space-4);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.crb-pager button {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--ui-border);
  border-radius: 50%;
  background: var(--ui-surface);
  color: var(--ui-text);
}
.crb-pager button:disabled {
  opacity: 0.4;
}

/* Drawer */
.crb-scrim {
  position: fixed;
  inset: 0;
  z-index: 1070;
  background: rgba(28, 25, 23, 0.4);
}
.crb-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1071;
  display: flex;
  flex-direction: column;
  width: min(520px, 100vw);
  background: var(--ui-surface-elevated, var(--ui-surface));
  box-shadow: var(--ui-shadow-pop);
  outline: none;
}
.crb-drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid var(--ui-border);
}
.crb-drawer-head h2 {
  margin: 8px 0 2px;
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
}
.crb-drawer-head p {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin: 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.crb-x {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border: 1px solid var(--ui-border);
  border-radius: 50%;
  background: var(--ui-surface);
  color: var(--ui-text-secondary);
  font-size: 18px;
}
.crb-drawer-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 6px 22px 22px;
}
.crb-drawer-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid var(--ui-border);
  background: var(--ui-surface);
}
.crb-drawer-foot .btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.crb-block {
  padding: 16px 0;
  border-bottom: 1px dashed var(--ui-border);
}
.crb-block:last-child {
  border-bottom: 0;
}
.crb-block h3 {
  margin: 0 0 12px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.crb-compare {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.crb-compare > i {
  align-self: center;
  color: var(--ui-text-faint);
}
.crb-compare > div {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
}
.crb-compare > div.is-new {
  border-color: var(--ui-accent);
  background: var(--ui-accent-soft);
}
.crb-compare span {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.crb-compare code {
  color: var(--ui-text);
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
  word-break: break-all;
}
.crb-compare small {
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
}
.crb-copy {
  position: absolute;
  top: 10px;
  right: 10px;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--ui-border);
  border-radius: 50%;
  background: var(--ui-surface);
  color: var(--ui-text-secondary);
}
.crb-reason {
  display: flex;
  gap: 8px;
  margin: 12px 0 0;
  padding: 10px 12px;
  border-left: 3px solid var(--ui-border-strong);
  border-radius: 0 var(--ui-radius-sm) var(--ui-radius-sm) 0;
  background: var(--ui-surface-sunken);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  white-space: pre-line;
}
.crb-reason i {
  flex: 0 0 auto;
  margin-top: 2px;
  color: var(--ui-text-faint);
}
.crb-checks {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--ui-fs-sm);
}
.crb-checks li {
  display: flex;
  align-items: center;
  gap: 8px;
}
.crb-checks i {
  font-size: 18px;
}
.crb-checks .is-ok i { color: var(--ui-success); }
.crb-checks .is-warn i { color: var(--ui-warning-strong); }
.crb-checks .is-bad { color: var(--ui-danger); font-weight: var(--ui-fw-bold); }
.crb-docgrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
.crb-doc {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.crb-doc small {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  text-align: center;
}
.crb-doc-open,
.crb-doc > .crb-doc-file {
  display: grid;
  place-items: center;
  aspect-ratio: 4 / 3;
  width: 100%;
  padding: 0;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
  overflow: hidden;
}
.crb-doc-open:hover {
  border-color: var(--ui-accent);
}
.crb-doc-open img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.crb-doc-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
}
.crb-doc-file i {
  font-size: 24px;
}
.crb-doc.is-missing .crb-doc-file {
  border-style: dashed;
  color: var(--ui-warning-strong);
}
.crb-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  padding: 10px 12px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
}
.crb-note.is-warn { background: var(--ui-warning-soft); color: var(--ui-warning-strong); }
.crb-note.is-ok { background: var(--ui-success-soft); color: var(--ui-success); }
.crb-note.is-bad { background: var(--ui-danger-soft); color: var(--ui-danger); }
.crb-fix {
  margin-bottom: 14px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
}
.crb-fix summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  cursor: pointer;
}
.crb-fix-grid {
  display: grid;
  gap: 10px;
  padding: 0 12px 12px;
}
.crb-fix-grid label,
label.crb-fix-grid {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
}
.crb-fix-grid span,
.crb-label {
  color: var(--ui-text);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.crb-label {
  display: block;
  margin-bottom: 6px;
}
.crb-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.crb-req {
  color: var(--ui-danger);
}
.crb-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.crb-quick button {
  padding: 5px 10px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
}
.crb-quick button:hover {
  border-color: var(--ui-danger);
  color: var(--ui-danger);
}
.crb-err {
  display: block;
  margin-top: 6px;
  color: var(--ui-danger);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
}
.crb-count {
  display: block;
  margin-top: 4px;
  color: var(--ui-text-faint);
  font-size: var(--ui-fs-xs);
  text-align: right;
}

/* Transitions */
.crb-fade-enter-active,
.crb-fade-leave-active {
  transition: opacity var(--ui-dur) var(--ui-ease);
}
.crb-fade-enter-from,
.crb-fade-leave-to {
  opacity: 0;
}
.crb-slide-enter-active,
.crb-slide-leave-active {
  transition: transform var(--ui-dur-slow) var(--ui-ease);
}
.crb-slide-enter-from,
.crb-slide-leave-to {
  transform: translateX(100%);
}

/* Responsive */
@media (max-width: 1199.98px) {
  .crb-row {
    grid-template-columns: 44px minmax(0, 1fr) auto 16px;
  }
  .crb-diff {
    grid-column: 2 / 4;
    grid-row: 2;
  }
  .crb-docs {
    display: none;
  }
}
@media (max-width: 767.98px) {
  .crb-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .crb-search {
    max-width: none;
  }
  .crb-select {
    flex: 1 1 100%;
  }
  .crb-drawer {
    top: auto;
    width: 100vw;
    max-height: 92vh;
    border-radius: var(--ui-radius-xl) var(--ui-radius-xl) 0 0;
  }
  .crb-slide-enter-from,
  .crb-slide-leave-to {
    transform: translateY(100%);
  }
  .crb-docgrid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (prefers-reduced-motion: reduce) {
  .crb-skel {
    animation: none;
  }
  .crb-slide-enter-active,
  .crb-slide-leave-active,
  .crb-fade-enter-active,
  .crb-fade-leave-active {
    transition: none;
  }
}
</style>
